import {
	Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy,
 ChangeDetectorRef, AfterViewInit, ElementRef
} from '@angular/core';
import { Observable, Subject } from 'rxjs/Rx';
import { MessageService } from 'base';
import { DateService } from 'base';
import { MonitorService } from 'monitor';
import { IdentityService } from 'identity';
import { NavigationService, NetFacadeService } from 'netview';
import { Chart } from 'chart.js';
import { InfoSetView, InfoSetViewEntry } from 'UvExplorerServer.Dtos.TS/Session';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';

export class SnmpIfHistoryEntry {
	public groupKey = "";
	public deviceName = "";
	public ipAddress = "";
	public ifName = "";
	public timestamp = "";
	public operStatus = "";
	public inBytes = "";
	public inUtilization = "";
	public outBytes = "";
	public outUtilization = "";
	public error = "";
	public ifIndex = "";
	public chartTimestamp: Date;
}

@Component({
	selector: 'snmpif-history',
	templateUrl: './snmpifhistory.component.html',
	styleUrls: ['./snmpifhistory.component.scss']
})
export class SnmpIfHistoryComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

	@Input()
	public visible: boolean;

	@Input()
	public monitorType: string;

	@Input()
	public deviceGuid: string;

	@Input()
	public monitorPolicyGuid: string;

	private data: InfoSetView;
	public entries: SnmpIfHistoryEntry[];
	public rowGroupMetadata: any;

	private destroyed: boolean;
	private eventsInterval: number;
	private timerId: any;

	private chart: Chart;
	private chartConfig: any;
	public chartHeight: number;

	private static readonly CHART_DEFAULT_HEIGHT = 400;
	private static readonly CHART_ZOOM_INCREMENT = 50;
	private static readonly CHART_DATASET_INCREMENT = 6;

	constructor(private element: ElementRef,
		private monitorService: MonitorService,
		private messageService: MessageService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private netFacadeService: NetFacadeService,
		private dateService: DateService) {

		this.monitorType = "";
		this.deviceGuid = "";
		this.monitorPolicyGuid = "";

		this.data = null;
		this.entries = [];
		this.rowGroupMetadata = {};

		this.destroyed = false;
		this.eventsInterval = 0;
		this.timerId = undefined;

		this.chart = null;
		this.chartConfig = undefined;
		this.resetChartHeight();
	}

	public formatDateHm(date: string | Date): string {
		return this.dateService.formatDateHm(date);
	}

	private resetChartHeight(): void {
		this.chartHeight = SnmpIfHistoryComponent.CHART_DEFAULT_HEIGHT;
		if (this.chartConfig) {
			let datasetCount = this.chartConfig.data.datasets.length;
			if (datasetCount > 25) {
				this.chartHeight += (datasetCount * SnmpIfHistoryComponent.CHART_DATASET_INCREMENT);
			}
		}
	}

	public ngOnInit(): void {
		if (this.visible && this.navService.isNetworkLoaded) {
			this.startTimer();
		}
	}

	public enableZoomInChart(): boolean {
		return true;
	}

	public enableZoomOutChart(): boolean {
		return (this.chartHeight > SnmpIfHistoryComponent.CHART_DEFAULT_HEIGHT);
	}

	public zoomInChart(): void {
		this.chartHeight += SnmpIfHistoryComponent.CHART_ZOOM_INCREMENT;
		this.delayedChartResize();
	}

	public zoomOutChart(): void {
		this.chartHeight -= SnmpIfHistoryComponent.CHART_ZOOM_INCREMENT;
		this.delayedChartResize();
	}

	private delayedChartResize(): void {
		setTimeout(() => {
			this.chart.resize();
		}, 0);
	}

	//https://stackoverflow.com/questions/1168260/algorithm-for-generating-unique-colors
	private colors: string[] = [
		"#000000",
		"#00FF00",
		"#0000FF",
		"#FF0000",
		"#01FFFE",
		"#FFA6FE",
		"#FFDB66",
		"#006401",
		"#010067",
		"#95003A",
		"#007DB5",
		"#FF00F6",
		"#FFEEE8",
		"#774D00",
		"#90FB92",
		"#0076FF",
		"#D5FF00",
		"#FF937E",
		"#6A826C",
		"#FF029D",
		"#FE8900",
		"#7A4782",
		"#7E2DD2",
		"#85A900",
		"#FF0056",
		"#A42400",
		"#00AE7E",
		"#683D3B",
		"#BDC6FF",
		"#263400",
		"#BDD393",
		"#00B917",
		"#9E008E",
		"#001544",
		"#C28C9F",
		"#FF74A3",
		"#01D0FF",
		"#004754",
		"#E56FFE",
		"#788231",
		"#0E4CA1",
		"#91D0CB",
		"#BE9970",
		"#968AE8",
		"#BB8800",
		"#43002C",
		"#DEFF74",
		"#00FFC6",
		"#FFE502",
		"#620E00",
		"#008F9C",
		"#98FF52",
		"#7544B1",
		"#B500FF",
		"#00FF78",
		"#FF6E41",
		"#005F39",
		"#6B6882",
		"#5FAD4E",
		"#A75740",
		"#A5FFD2",
		"#FFB167",
		"#009BFF",
		"#E85EBE",
	];

	public ngAfterViewInit(): void {

	}

	private rebuildChart(): void {

		if (!this.chart) {
			this.chartConfig = {
				type: 'line',
				data: {
					labels: [],
					datasets: []
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					title: {
						display: false,
						text: 'SNMP IF History'
					},
					tooltips: {
						mode: 'index',
						intersect: false,
					},
					hover: {
						mode: 'nearest',
						intersect: true
					},
					scales: {
						xAxes: [{
							display: true,
							scaleLabel: {
								display: false,
								labelString: 'Time'
							}
						}],
						yAxes: [{
							display: true,
							scaleLabel: {
								display: false,
								labelString: '%'
							}
						}]
					},
					legend: {
						display: true,
						position: "bottom"
					}
				}
			};

			let rootElem = <HTMLDivElement>this.element.nativeElement;
			let canvas = <HTMLCanvasElement>rootElem.querySelector("#graph-canvas");
			let ctx = canvas.getContext('2d');
			this.chart = new Chart(ctx, this.chartConfig);
		}

		// Calculate chart labels
		let labelList: Date[] = [];
		this.entries.forEach((entry: SnmpIfHistoryEntry) => {
			if (entry.chartTimestamp) {
				let chartTimestamp = entry.chartTimestamp;

				let found = false;
				for (let i = 0; i < labelList.length; ++i) {
					if (chartTimestamp.getTime() == labelList[i].getTime()) {
						found = true;
						break;
					}
				}

				if (!found) {
					labelList.push(chartTimestamp);
				}
			}
		});

		labelList.sort((a: Date, b: Date) => {
			if (a < b) {
				return -1;
			}
			else if (a > b) {
				return 1;
			}
			else {
				return 0;
			}
		});

		// Install labels in chart
		this.chart.data.labels = [];
		labelList.forEach((chartLabel: Date) => {
			this.chart.data.labels.push(this.dateService.formatTimeHm(chartLabel));
		});

		// Group entries by (device, ip)
		let groups: Object = {};
		this.entries.forEach((entry: SnmpIfHistoryEntry) => {
			if (!groups[entry.groupKey]) {
				groups[entry.groupKey] = [];
			}
			groups[entry.groupKey].push(entry);
		});

		// Plot data for each (device, ip, ifname) triple
		this.chart.data.datasets = [];
		let colorIndex = 0;
		for (let groupKey in groups) {

			let group: SnmpIfHistoryEntry[] = groups[groupKey];

			for (let x = 0; x < 2; ++x) {

				let label = group[0].deviceName + " - " + group[0].ifName;

				switch (x) {
					case 0:
						label += " (IN)";
						break;
					case 1:
						label += " (OUT)";
						break;
					default:
						console.assert(false);
						break;
				}

				let dataset = {
					label: label,
					fill: false,
					spanGaps: false,
					backgroundColor: this.colors[colorIndex],
					borderColor: this.colors[colorIndex],
					data: [],
				};

				labelList.forEach((chartLabel: Date) => {
					let entryForLabel: SnmpIfHistoryEntry = null;
					for (let i = 0; i < group.length; ++i) {
						if (group[i].chartTimestamp) {
							if (group[i].chartTimestamp.getTime() == chartLabel.getTime()) {
								entryForLabel = group[i];
								break;
							}
						}
					}

					if (!!entryForLabel && x == 0 && entryForLabel.inUtilization !== "") {
						dataset.data.push(parseFloat(entryForLabel.inUtilization));
					}
					else if (!!entryForLabel && x == 1 && entryForLabel.outUtilization !== "") {
						dataset.data.push(parseFloat(entryForLabel.outUtilization));
					}
					else {
						dataset.data.push(NaN);
					}
				});

				colorIndex = (colorIndex + 1) % this.colors.length;

				this.chart.data.datasets.push(dataset);
			}
		}

		this.chart.update();
//		this.chart.resize();
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (this.destroyed)
			return;

		this.stopTimer();
		if (this.visible && this.navService.isNetworkLoaded) {
			this.retrieveMonitorHistory(true).subscribe(
				(result: boolean) => {
					this.resetChartHeight();
					this.delayedChartResize();
				},
				(error) => {
					this.resetChartHeight();
					this.delayedChartResize();
				});
			this.startTimer();
		}
	}

	public ngOnDestroy(): void {
		this.stopTimer();
	}

	private setEventsInterval(): void {
		this.eventsInterval = 0;

		let netViewGuid = this.navService.location.netViewGuid;
		let network: NetViewDto = this.netFacadeService.findNetwork(netViewGuid);
		if (network) {
			this.eventsInterval = network.EventsInterval;
		}
		else {
			this.messageService.displayErrorMessage("Could not set events interval");
		}
	}

	private startTimer(): void {

		this.setEventsInterval();

		if (!this.timerId && this.visible && this.eventsInterval > 0) {
			let timeoutMillis = this.eventsInterval * 1000;
			this.timerId = setInterval(() => {
				this.retrieveMonitorHistory(false);
			}, timeoutMillis);
		}
	}

	private stopTimer(): void {
		if (this.timerId) {
			clearInterval(this.timerId);
			this.timerId = undefined;
		}
	}

	private retrieveMonitorHistory(showBusy: boolean): Observable<boolean> {

		if (!!this.monitorType && (!!this.deviceGuid || !!this.monitorPolicyGuid)) {

			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			let subject = new Subject<any>();

			this.monitorService.getMonitorHistory(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
				schedDiscRsltGuid, this.monitorType, this.monitorPolicyGuid, this.deviceGuid, showBusy).subscribe(
					(result: InfoSetView) => {
						this.data = result;
						this.rebuildEntries();
						subject.next(true);
					},
					(error) => {
						this.data = null;
						this.rebuildEntries();
						this.messageService.displayErrorMessage("Could not retrieve monitor history");
						subject.error(false);
					});

			return subject;
		}
		else {
			this.data = null;
			this.rebuildEntries();
			return new Observable<boolean>(observer => {
				observer.error(false);
			});
		}
	}

	private rebuildEntries(): void {
		this.entries = [];

		if (!!this.data) {
			this.data.entries.forEach((isvEntry: InfoSetViewEntry) => {
				let entry = new SnmpIfHistoryEntry();
				entry.deviceName = isvEntry.values[0].value;
				entry.ipAddress = isvEntry.values[1].value;
				entry.ifName = isvEntry.values[2].value;
				entry.timestamp = isvEntry.values[3].value;
				entry.operStatus = isvEntry.values[4].value;
				entry.inBytes = isvEntry.values[5].value;
				entry.inUtilization = isvEntry.values[6].value;
				entry.outBytes = isvEntry.values[7].value;
				entry.outUtilization = isvEntry.values[8].value;
				entry.error = isvEntry.values[9].value;
				entry.ifIndex = isvEntry.values[10].value;
				entry.groupKey = isvEntry.groupKey;
				if (entry.timestamp) {
					entry.chartTimestamp = this.dateService.roundDateToNearestMinute(new Date(entry.timestamp));
				}
				else {
					entry.chartTimestamp = undefined;
				}

				this.entries.push(entry);
			});
		}

		this.rebuildRowGroupMetaData();
		this.rebuildChart();
	}

	private rebuildRowGroupMetaData() {
		this.rowGroupMetadata = {};
		if (!!this.data) {
			for (let i = 0; i < this.entries.length; i++) {
				let rowData = this.entries[i];
				let groupKey = rowData.groupKey;
				if (i == 0) {
					this.rowGroupMetadata[groupKey] = { index: 0, size: 1 };
				}
				else {
					let previousRowData = this.entries[i - 1];
					let previousRowGroup = previousRowData.groupKey;
					if (groupKey === previousRowGroup) {
						console.assert(!!this.rowGroupMetadata[groupKey]);
						this.rowGroupMetadata[groupKey].size++;
					}
					else {
						console.assert(!this.rowGroupMetadata[groupKey]);
						this.rowGroupMetadata[groupKey] = { index: i, size: 1 };
					}
				}
			}
		}
	}

}
